import React, { useCallback, useEffect, useMemo, useState } from "react";
import SectionCard from "@/components/shared/SectionCard";
import MapView from "@/components/shared/MapView";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { downloadReport } from "@/lib/downloadReport";
import api, { extractApiError, unwrapSingle } from "@/lib/api";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip } from "recharts";
import { toast } from "sonner";
import { 
  Truck, LineChart as LineIcon, Download, Car, User, Info, Loader2, RefreshCw
} from "lucide-react";
import VehicleProfileDialog from "@/components/shared/VehicleProfileDialog";
import { useLocation, useNavigate } from "react-router-dom";

const STATUS_OPTIONS = ["All", "Completed", "Ongoing"];

function normalizeCollection(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function toIso(value) {
  if (!value) return undefined;
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return undefined;
  return dt.toISOString();
}

function formatDate(value) {
  if (!value) return "—";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);
  return dt.toLocaleString();
}

function toTitleCase(value) {
  const text = String(value || "");
  if (!text) return "—";
  return text
    .toLowerCase()
    .split("_")
    .map((item) => item.charAt(0).toUpperCase() + item.slice(1))
    .join(" ");
}

function toText(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (typeof value === "object") {
    const lat = value?.lat ?? value?.latitude;
    const lon = value?.lon ?? value?.lng ?? value?.longitude;
    if (Number.isFinite(Number(lat)) && Number.isFinite(Number(lon))) {
      return `${Number(lat).toFixed(6)}, ${Number(lon).toFixed(6)}`;
    }

    if (typeof value?.name === "string" && value.name.trim()) return value.name;
    if (typeof value?.address === "string" && value.address.trim()) return value.address;
    if (typeof value?.label === "string" && value.label.trim()) return value.label;

    try {
      return JSON.stringify(value);
    } catch {
      return "—";
    }
  }

  return String(value);
}

function formatDuration(item) {
  const secs = Number(item?.duration_seconds ?? item?.duration_sec ?? item?.duration ?? 0);
  if (secs > 0) {
    const hh = Math.floor(secs / 3600);
    const mm = Math.floor((secs % 3600) / 60);
    if (hh > 0) return `${hh}h ${mm}m`;
    return `${mm}m`;
  }

  const mins = Number(item?.duration_minutes ?? 0);
  if (mins > 0) {
    const hh = Math.floor(mins / 60);
    const mm = mins % 60;
    if (hh > 0) return `${hh}h ${mm}m`;
    return `${mm}m`;
  }

  return "—";
}

function normalizeTrip(item) {
  const summary = item?.summary || {};
  const rawFrom = item?.from || item?.start_location || item?.origin;
  const rawTo = item?.to || item?.end_location || item?.destination;
  const distanceKmFromMeters = Number(item?.distance_meters ?? 0) > 0
    ? Number(item.distance_meters) / 1000
    : 0;
  const distanceKmRaw = Number(item?.distance_km ?? item?.total_distance_km ?? summary?.distance_km ?? 0);
  return {
    id: item?.id || item?.trip_id || "",
    vehicleId: item?.vehicle?.id || item?.vehicle?.vehicle_id || item?.vehicle_id || "",
    vehicleRegistration:
      item?.vehicle?.registration_number
      || item?.registration_number
      || item?.vehicle_registration_number
      || "—",
    driverName: item?.driver?.name || item?.driver_name || "Unassigned",
    status: String(item?.status || "").toUpperCase(),
    statusLabel: toTitleCase(item?.status || "—"),
    from: toText(rawFrom),
    to: toText(rawTo),
    startedAt: item?.started_at || item?.start_time || item?.created_at || "",
    endedAt: item?.ended_at || item?.completed_at || item?.end_time || item?.updated_at || "",
    distanceKm: distanceKmRaw > 0 ? distanceKmRaw : (distanceKmFromMeters > 0 ? Number(distanceKmFromMeters.toFixed(2)) : null),
    avgSpeedKmh:
      Number(summary?.avg_speed_kmh ?? item?.avg_speed_kmh ?? 0)
      || 0,
    maxSpeedKmh:
      Number(summary?.max_speed_kmh ?? item?.max_speed_kmh ?? 0)
      || 0,
    totalPoints:
      Number(summary?.total_points ?? item?.total_points ?? 0)
      || 0,
    durationLabel: formatDuration(item),
    harshEvents: Number(item?.harsh_events ?? item?.event_count ?? 0) || 0,
    score: Number(item?.score ?? item?.driver_score ?? 0) || 0,
    violations: Array.isArray(item?.violations) ? item.violations.map((entry) => toText(entry)) : [],
    raw: item,
  };
}

function normalizeVehicle(item) {
  return {
    id: item?.id || item?.vehicle_id || "",
    registrationNumber: item?.registration_number || item?.plate || item?.vehicle_number || "—",
  };
}

function normalizeTelemetryPoint(item) {
  const lat = Number(item?.latitude ?? item?.lat ?? item?.location?.lat ?? item?.gps?.lat);
  const lon = Number(item?.longitude ?? item?.lng ?? item?.lon ?? item?.location?.lon ?? item?.gps?.lon);
  return {
    lat,
    lon,
    speed: Number(item?.speed_kmh ?? item?.speed ?? 0) || 0,
    ts: item?.timestamp || item?.recorded_at || item?.time || null,
  };
}

export default function PoliceVehicleActivities() {
  const location = useLocation();
  const navigate = useNavigate();

  const [selectedTripId, setSelectedTripId] = useState(null);
  const [expandedTripId, setExpandedTripId] = useState(null);
  const [isMasterViewOpen, setIsMasterViewOpen] = useState(false);
  const [masterVehiclePlate, setMasterVehiclePlate] = useState(null);

  const [tripsData, setTripsData] = useState([]);
  const [vehiclesData, setVehiclesData] = useState([]);
  const [tripDetailById, setTripDetailById] = useState({});
  const [tripTelemetryById, setTripTelemetryById] = useState({});

  const [loadingTrips, setLoadingTrips] = useState(false);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [loadingTripDetailId, setLoadingTripDetailId] = useState("");
  const [loadingTelemetryId, setLoadingTelemetryId] = useState("");
  const [error, setError] = useState("");

  const [meta, setMeta] = useState({ total: 0, page: 1, page_size: 20, pages: 1 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [filters, setFilters] = useState({
    query: "",
    status: "Completed",
    vehicle: "All",
    driver: "All",
    violationsOnly: false,
    fromDt: "",
    toDt: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({
    status: "Completed",
    vehicle: "All",
    fromDt: "",
    toDt: "",
  });

  const fetchVehicles = useCallback(async () => {
    setLoadingVehicles(true);
    try {
      const { data } = await api.get("/vehicles", {
        params: { is_active: true, page_size: 100 },
      });
      const list = normalizeCollection(data).map(normalizeVehicle).filter((item) => item.id);
      setVehiclesData(list);
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to load vehicles");
      setVehiclesData([]);
    } finally {
      setLoadingVehicles(false);
    }
  }, []);

  const fetchTrips = useCallback(async () => {
    setLoadingTrips(true);
    setError("");
    try {
      const params = {
        page,
        page_size: pageSize,
      };

      if (appliedFilters.vehicle !== "All") params.vehicle_id = appliedFilters.vehicle;
      if (appliedFilters.status !== "All") {
        params.status = appliedFilters.status === "Completed" ? "COMPLETED" : "ACTIVE";
      }

      const fromIso = toIso(appliedFilters.fromDt);
      const toIsoValue = toIso(appliedFilters.toDt);
      if (fromIso) params.from_dt = fromIso;
      if (toIsoValue) params.to_dt = toIsoValue;

      const { data } = await api.get("/trips", { params });
      const list = normalizeCollection(data).map(normalizeTrip).filter((item) => item.id);
      setTripsData(list);
      setMeta(
        data?.meta || {
          total: list.length,
          page,
          page_size: pageSize,
          pages: 1,
        },
      );

      if (!selectedTripId && list.length) setSelectedTripId(list[0].id);
    } catch (err) {
      const { message } = extractApiError(err);
      setError(message);
      setTripsData([]);
      setMeta({ total: 0, page, page_size: pageSize, pages: 1 });
    } finally {
      setLoadingTrips(false);
    }
  }, [appliedFilters.fromDt, appliedFilters.status, appliedFilters.toDt, appliedFilters.vehicle, page, pageSize, selectedTripId]);

  const fetchTripDetailAndTelemetry = useCallback(async (tripId) => {
    if (!tripId) return;

    if (!tripDetailById[tripId]) {
      setLoadingTripDetailId(tripId);
      try {
        const response = await api.get(`/trips/${tripId}`);
        setTripDetailById((prev) => ({ ...prev, [tripId]: normalizeTrip(unwrapSingle(response) || {}) }));
      } catch (err) {
        const { message } = extractApiError(err);
        toast.error(message || "Failed to load trip detail");
      } finally {
        setLoadingTripDetailId("");
      }
    }

    if (!tripTelemetryById[tripId]) {
      setLoadingTelemetryId(tripId);
      try {
        const { data } = await api.get(`/trips/${tripId}/telemetry`, {
          params: { page: 1, page_size: 500 },
        });
        const telemetryList = normalizeCollection(data)
          .map(normalizeTelemetryPoint)
          .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lon));

        setTripTelemetryById((prev) => ({
          ...prev,
          [tripId]: telemetryList,
        }));
      } catch (err) {
        const { message } = extractApiError(err);
        toast.error(message || "Failed to load telemetry");
      } finally {
        setLoadingTelemetryId("");
      }
    }
  }, [tripDetailById, tripTelemetryById]);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  useEffect(() => {
    fetchTrips();
  }, [fetchTrips]);

  useEffect(() => {
    const tripId = new URLSearchParams(location.search).get("trip_id");
    if (!tripId) return;

    setSelectedTripId(tripId);
    setExpandedTripId(tripId);
    fetchTripDetailAndTelemetry(tripId);

    const params = new URLSearchParams(location.search);
    params.delete("trip_id");
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  }, [fetchTripDetailAndTelemetry, location.pathname, location.search, navigate]);

  const filteredTrips = useMemo(() => tripsData.filter((trip) => {
    const needle = filters.query.trim().toLowerCase();
    const matchesQuery =
      !needle
      || trip.id.toLowerCase().includes(needle)
      || trip.vehicleRegistration.toLowerCase().includes(needle)
      || trip.driverName.toLowerCase().includes(needle);

    const matchesDriver = filters.driver === "All" || trip.driverName === filters.driver;
    const matchesViolations = !filters.violationsOnly || (trip.violations?.length > 0);

    return matchesQuery && matchesDriver && matchesViolations;
  }), [filters.driver, filters.query, filters.violationsOnly, tripsData]);

  const selectedTripFromList = filteredTrips.find((trip) => trip.id === selectedTripId) || filteredTrips[0] || null;
  const selectedTrip = (selectedTripId && tripDetailById[selectedTripId]) || selectedTripFromList;

  const selectedTelemetry = selectedTripId ? (tripTelemetryById[selectedTripId] || []) : [];
  const routeForMap = selectedTelemetry.length > 1
    ? selectedTelemetry.map((point) => [point.lat, point.lon])
    : null;
  const routeCenter = routeForMap?.[0] || [19.076, 72.8777];

  const speedData = selectedTelemetry.length
    ? selectedTelemetry.slice(0, 24).map((point, index) => ({
      label: point.ts ? new Date(point.ts).toLocaleTimeString() : `P${index + 1}`,
      speed: point.speed,
    }))
    : [{ label: "No Data", speed: selectedTrip?.avgSpeedKmh || 0 }];

  const dailyUsage = [
    { day: "Mon", trips: 7 },
    { day: "Tue", trips: 9 },
    { day: "Wed", trips: 8 },
    { day: "Thu", trips: 10 },
    { day: "Fri", trips: 11 },
    { day: "Sat", trips: 5 },
    { day: "Sun", trips: 4 },
  ];

  const handleExport = () => {
    downloadReport({
      filename: "police_vehicle_activities",
      title: "Vehicle Activities Report",
      headers: ["Vehicle", "Driver", "Distance (km)", "Avg Speed", "Max Speed", "Status"],
      rows: filteredTrips.map((trip) => [
        trip.vehicleRegistration,
        trip.driverName,
        trip.distanceKm ?? "—",
        `${trip.avgSpeedKmh} km/h`,
        `${trip.maxSpeedKmh} km/h`,
        trip.statusLabel,
      ]),
    });
    toast.success("Activities exported");
  };

  const applyServerFilters = () => {
    if (filters.fromDt && filters.toDt && new Date(filters.fromDt) > new Date(filters.toDt)) {
      toast.error("From date cannot be later than To date");
      return;
    }

    setPage(1);
    setAppliedFilters({
      status: filters.status,
      vehicle: filters.vehicle,
      fromDt: filters.fromDt,
      toDt: filters.toDt,
    });
  };

  const resetServerFilters = () => {
    setFilters((prev) => ({
      ...prev,
      status: "Completed",
      vehicle: "All",
      fromDt: "",
      toDt: "",
      driver: "All",
      query: "",
      violationsOnly: false,
    }));

    setPage(1);
    setAppliedFilters({
      status: "Completed",
      vehicle: "All",
      fromDt: "",
      toDt: "",
    });
  };

  const handleOpenMasterView = (vehiclePlate) => {
    setMasterVehiclePlate(vehiclePlate);
    setIsMasterViewOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="police-vehicle-activities">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Vehicle Activities</h1>
          <p className="text-sm text-slate-500">Historical route analytics, trip performance, and driver behavior insights for patrol fleets.</p>
        </div>
        <Button variant="outline" onClick={handleExport}>
          <Download size={16} className="mr-2" /> Export CSV
        </Button>
      </div>

      <SectionCard title="Activity Filters" subtitle="Refine historical trips by vehicle, driver, status and violations.">
        <div className="grid gap-3 lg:grid-cols-6">
          <Input
            placeholder="Vehicle ID, driver, trip"
            value={filters.query}
            onChange={(event) => setFilters((prev) => ({ ...prev, query: event.target.value }))}
          />

          {/* <div className="space-y-1 position-relative">
            <Label className="text-[11px] text-slate-500 floating-lbl">From</Label>
            <Input type="datetime-local" value={filters.fromDt} onChange={(event) => setFilters((prev) => ({ ...prev, fromDt: event.target.value }))} />
          </div>

          <div className="space-y-1 position-relative">
            <Label className="text-[11px] text-slate-500 floating-lbl">To</Label>
            <Input type="datetime-local" value={filters.toDt} onChange={(event) => setFilters((prev) => ({ ...prev, toDt: event.target.value }))} />
          </div> */}

          <Select value={filters.status} onValueChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}>
            <SelectTrigger className="w-full"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}
            </SelectContent>
          </Select>

          {/* <Select value={filters.driver} onValueChange={(value) => setFilters((prev) => ({ ...prev, driver: value }))}>
            <SelectTrigger className="w-full"><SelectValue placeholder="Driver" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All</SelectItem>
              {[...new Set(tripsData.map((trip) => trip.driverName).filter(Boolean))].map((driver) => (
                <SelectItem key={driver} value={driver}>{driver}</SelectItem>
              ))}
            </SelectContent>
          </Select> */}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-slate-600">
          <label className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 cursor-pointer hover:bg-slate-50 transition-colors">
            <input
              type="checkbox"
              className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              checked={filters.violationsOnly}
              onChange={(event) => setFilters((prev) => ({ ...prev, violationsOnly: event.target.checked }))}
            />
            <span className="font-medium">Violations only</span>
          </label>
          <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200 px-3 py-1.5 rounded-full font-bold">
            Showing {filteredTrips.length} trips
          </Badge>

          <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200 px-3 py-1.5 rounded-full font-bold">
            Page {meta.page || page} of {meta.pages || 1}
          </Badge>

          <Button size="sm" variant="ghost" onClick={resetServerFilters}>
            Reset
          </Button>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={fetchTrips} disabled={loadingTrips || loadingVehicles}>
            <RefreshCw size={13} className={loadingTrips ? "animate-spin" : ""} /> Refresh
          </Button>

          <div className="ml-2 flex items-center gap-2">
            <Select
              value={String(pageSize)}
              onValueChange={(value) => {
                setPage(1);
                setPageSize(Number(value));
              }}
            >
              <SelectTrigger className="w-[110px]"><SelectValue placeholder="Size" /></SelectTrigger>
              <SelectContent>
                {[10, 20, 50].map((size) => (
                  <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button variant="outline" disabled={loadingTrips || page <= 1} onClick={() => setPage((prev) => Math.max(1, prev - 1))}>
              Previous
            </Button>
            <Button
              variant="outline"
              disabled={loadingTrips || page >= (meta.pages || 1)}
              onClick={() => setPage((prev) => Math.min(meta.pages || 1, prev + 1))}
            >
              Next
            </Button>
          </div>

          {filters.query.length >= 4 && tripsData.some((trip) => trip.vehicleRegistration.includes(filters.query.toUpperCase())) && (
            <Button 
              size="sm" 
              variant="default" 
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-4"
              onClick={() => {
                const target = tripsData.find((trip) => trip.vehicleRegistration.includes(filters.query.toUpperCase()));
                if (target) handleOpenMasterView(target.vehicleRegistration);
              }}
            >
              <User size={14} className="mr-2" /> View Vehicle Profile
            </Button>
          )}
        </div>
      </SectionCard>

      {error && <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

      <div className="grid gap-6 xl:grid-cols-[1.4fr_1fr]">
        <div className="space-y-6 ">
          <div className="height_cus">
          <SectionCard title="Trip Log" subtitle="Detailed vehicle trips with distance, speed, and safety events.">
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
              <div className="grid grid-cols-6 gap-4 border-b border-slate-200 bg-slate-50 px-4 py-3 text-xs uppercase tracking-[.2em] text-slate-500">
                <span>Vehicle</span>  
                <span>Driver</span>
                <span>Distance</span>
                <span>Avg Speed</span>
                <span>Status</span>
                <span className="text-right">Actions</span>
              </div>
              <div className="divide-y divide-slate-200">
                {loadingTrips && (
                  <div className="px-4 py-10 text-center text-sm text-slate-500">
                    <Loader2 size={16} className="mr-2 inline animate-spin" /> Loading trips...
                  </div>
                )}

                {!loadingTrips && filteredTrips.length === 0 && (
                  <div className="px-4 py-10 text-center text-sm text-slate-500">No trips found.</div>
                )}

                {!loadingTrips && filteredTrips.map((trip, index) => {
                  const isExpanded = expandedTripId === trip.id;
                  const telemetry = tripTelemetryById[trip.id] || [];
                  const detail = tripDetailById[trip.id] || trip;
                  const loadingExpand = loadingTripDetailId === trip.id || loadingTelemetryId === trip.id;

                  return (
                    <div key={trip.id}>
                      <div
                        className="grid grid-cols-6 gap-4 px-4 py-4 hover:bg-slate-50 cursor-pointer"
                        onClick={() => {
                          setSelectedTripId(trip.id);
                          fetchTripDetailAndTelemetry(trip.id);
                        }}
                      >
                        <div
                          className="text-sm font-bold text-blue-600 hover:underline cursor-pointer flex items-center gap-1.5"
                          onClick={() => handleOpenMasterView(trip.vehicleRegistration)}
                        >
                          <Car size={14} className="text-slate-400" />
                          {trip.vehicleRegistration}
                        </div>
                        <div className="text-sm text-slate-700">{trip.driverName}</div>
                        <div className="text-sm text-slate-700">{trip.distanceKm ? `${trip.distanceKm} km` : "—"}</div>
                        <div className="text-sm text-slate-700">{trip.avgSpeedKmh} km/h</div>
                        <div>
                          <Badge
                            variant="outline"
                            className={trip.status === "ONGOING" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}
                          >
                            {trip.statusLabel}
                          </Badge>
                        </div>
                        <div className="text-right flex items-center justify-end gap-2">
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 rounded-full" onClick={(e) => { e.stopPropagation(); handleOpenMasterView(trip.vehicleRegistration); }}>
                            <Info size={14} className="text-blue-600" />
                          </Button>
                          <Button
                            size="sm"
                            variant={selectedTripId === trip.id ? "secondary" : "outline"}
                            onClick={async (e) => {
                              e.stopPropagation();
                              setSelectedTripId(trip.id);
                              setExpandedTripId((prev) => (prev === trip.id ? null : trip.id));
                              await fetchTripDetailAndTelemetry(trip.id);
                            }}
                          >
                            {isExpanded ? "Collapse" : "Expand"}
                          </Button>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="border-t border-slate-100 bg-slate-50/70 px-4 py-3 text-sm">
                          {loadingExpand ? (
                            <div className="text-slate-500">
                              <Loader2 size={14} className="mr-2 inline animate-spin" /> Loading trip detail and route replay...
                            </div>
                          ) : (
                            <div className="grid gap-2 md:grid-cols-3">
                              <div><strong>Route:</strong> {detail.from} → {detail.to}</div>
                              <div><strong>Max speed:</strong> {detail.maxSpeedKmh} km/h</div>
                              <div><strong>Telemetry points:</strong> {telemetry.length}</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            </SectionCard>
          </div>

          <SectionCard title="Trip Details" subtitle="Route, stops, timeline and safety events for the selected trip.">
            {!selectedTrip ? (
              <div className="text-sm text-slate-500">Select a trip to view details.</div>
            ) : (
            <div className="space-y-5">
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <p className="text-xs uppercase tracking-[.2em] text-slate-500">Route</p>
                    <p className="mt-2 text-base font-semibold text-slate-900">{selectedTrip.from} → {selectedTrip.to}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[.2em] text-slate-500">Driver score</p>
                    <p className="mt-2 text-base font-semibold text-slate-900">{selectedTrip.score || selectedTrip.totalPoints}%</p>
                  </div>
                </div>
                <div className="mt-4 grid gap-4 sm:grid-cols-3 text-sm text-slate-600">
                  <div><strong>Duration:</strong> {selectedTrip.durationLabel}</div>
                  <div><strong>Max speed:</strong> {selectedTrip.maxSpeedKmh} km/h</div>
                  <div><strong>Harsh events:</strong> {selectedTrip.harshEvents}</div>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-3xl border border-slate-200 bg-white p-4">
                  <h3 className="text-sm font-semibold text-slate-900">Driving Timeline</h3>
                  <div className="mt-3 space-y-3 text-sm text-slate-600">
                    <div className="rounded-2xl bg-slate-50 p-3">Start {formatDate(selectedTrip.startedAt)} at {selectedTrip.from}</div>
                    <div className="rounded-2xl bg-slate-50 p-3">Avg speed {selectedTrip.avgSpeedKmh} km/h · Max speed {selectedTrip.maxSpeedKmh} km/h</div>
                    <div className="rounded-2xl bg-slate-50 p-3">Finish {formatDate(selectedTrip.endedAt)} at {selectedTrip.to}</div>
                  </div>
                </div>
                <div className="rounded-3xl border border-slate-200 bg-white p-4">
                  <h3 className="text-sm font-semibold text-slate-900">Violations & Events</h3>
                  <div className="mt-3 space-y-2 text-sm text-slate-600">
                    {selectedTrip.violations.length === 0 ? (
                      <div className="rounded-2xl bg-emerald-50 p-3 text-emerald-700">No violations recorded.</div>
                    ) : selectedTrip.violations.map((violation, index) => (
                      <div key={`${index}-${violation}`} className="rounded-2xl bg-rose-50 p-3 text-rose-700">{violation}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            )}
          </SectionCard>
        </div>

        <div className="space-y-6">
          <SectionCard title="Route Map" subtitle="Selected trip path rendered on a responsive operational map.">
            <MapView
              markers={[]}
              route={routeForMap}
              center={routeCenter}
              zoom={12}
              height={320}
              testId="vehicle-activity-map"
            />
          </SectionCard>

          <SectionCard title="Analytics" subtitle="Speed trends, daily vehicle workload and performance scoring.">
            <div className="grid gap-4">
              <div className="rounded-3xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">Speed Trend</h3>
                    <p className="text-xs text-slate-500">Average speed per navigation segment.</p>
                  </div>
                  <LineIcon size={18} className="text-slate-400" />
                </div>
                <div className="mt-4 h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={speedData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="label" tickLine={false} axisLine={false} />
                      <YAxis tickLine={false} axisLine={false} />
                      <Tooltip />
                      <Line type="monotone" dataKey="speed" stroke="#2563EB" strokeWidth={3} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="rounded-3xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">Daily Usage</h3>
                    <p className="text-xs text-slate-500">Trips completed per day.</p>
                  </div>
                  <Truck size={18} className="text-slate-400" />
                </div>
                <div className="mt-4 h-44">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dailyUsage}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="day" tickLine={false} axisLine={false} />
                      <YAxis tickLine={false} axisLine={false} />
                      <Tooltip />
                      <Bar dataKey="trips" fill="#2563EB" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </SectionCard>
        </div>
      </div>
      <VehicleProfileDialog 
        open={isMasterViewOpen} 
        onOpenChange={setIsMasterViewOpen} 
        vehiclePlate={masterVehiclePlate} 
      />
    </div>
  );
}
